import { PIM_AGENT_USER_ROLE } from '@src/_new/modules/agent-tasks/roles';
import { useCanAccessHierarchies } from '@src/_new/modules/auth/canAccessHierarchies';
import { getDefaultRoute } from '@src/_new/modules/auth/getDefaultRoute';
import { CPQ_PRICING_DEBUG_ROLE } from '@src/_new/modules/cpq/components/PricingTrace';
import { useCustomPages } from '@src/_new/modules/custom-pages/hooks/useCustomPages';
import { useLanguageCtx } from '@src/_new/modules/language';
import { RouteErrorPage } from '@src/_new/modules/ui';
import { AnalyticsProvider } from '@src/_new/modules/user-analytics';
import { useCompanyOverrides } from '@src/_new/overrides/hooks/useCompanyOverrides';
import { EditNodeRouteProvider } from '@src/components/hierarchies/shared/EditNodeRouteContext';
import { path as translationsPath } from '@src/components/translations/Translations';
import { useAuth } from '@src/hooks';
import Layout from '@src/Layout';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { lazy, Suspense, useContext } from 'react';
import {
    createBrowserRouter,
    createRoutesFromElements,
    Navigate,
    Route,
    RouterProvider,
    useLocation,
    useParams,
} from 'react-router-dom';

import { DelayedFallback } from './DelayedFallback';

const LanguageCreatePage = lazy(() => import('@pages/LanguageCreatePage'));
const LanguageEditPage = lazy(() => import('@pages/LanguageEditPage'));
const LanguageListPage = lazy(() => import('@pages/LanguageListPage'));
const SearchListPage = lazy(() => import('@pages/SearchListPage'));
const SearchDetailPage = lazy(() => import('@pages/SearchDetailPage'));
const ShortListPage = lazy(() => import('@pages/ShortListPage'));
const PrototypesPage = lazy(() => import('@pages/PrototypesPage'));
const ProductsPage = lazy(() => import('@pages/ProductsPage'));
const JobListPage = lazy(() => import('@pages/JobListPage'));
const JobSchedulesPage = lazy(() => import('@pages/JobSchedulesPage'));

const ThreeDViewerPage = lazy(() => import('@pages/ThreeDViewerPage'));
// todo
const Hierarchies = lazy(() => import('@src/modules/hierarchy/HierarchyViewer'));
const Discounts = lazy(() => import('@src/modules/discounts/Discounts'));
const Translations = lazy(() => import('@src/components/translations/Translations'));
const SettingsPage = lazy(() => import('@pages/SettingsPage'));
const JsonataDependencies = lazy(
    () => import('@src/_new/modules/jsonata-dependencies/JsonataDependencies'),
);
const DataQualityPage = lazy(() => import('@pages/DataQualityPage'));
const OffersPage = lazy(() => import('@pages/OffersPage'));
const OfferDetailPage = lazy(() => import('@pages/OfferDetailPage'));
const OrdersPage = lazy(() => import('@pages/OrdersPage'));
const OrderDetailPage = lazy(() => import('@pages/OrderDetailPage'));
const CustomPageListPage = lazy(() => import('@pages/CustomPageListPage'));
const CustomPageDetailPage = lazy(() => import('@pages/CustomPageDetailPage'));
const AdminDashboardPage = lazy(() => import('@pages/AdminDashboardPage'));
const CompanyAdminPage = lazy(() => import('@pages/CompanyAdminPage'));
const ConfigitPage = lazy(() => import('@pages/ConfigitPage'));
const ExternalCallLogPage = lazy(() => import('@pages/ExternalCallLogPage'));
const UserAnalyticsPage = lazy(() => import('@pages/UserAnalyticsPage'));
const BulkChangeRulesPage = lazy(() => import('@pages/BulkChangeRulesPage'));
const PricingTestBenchPage = lazy(() => import('@pages/PricingTestBenchPage'));
const AgentTasksPage = lazy(() => import('@pages/AgentTasksPage'));
const AgentRunDetailPage = lazy(() => import('@pages/AgentRunDetailPage'));
const UserProfilePage = lazy(() => import('@pages/UserProfilePage'));

/** A link to an order in the custom page form (`/pages/orders/<id>`) lands on the native page. */
function LegacyOrderDetailRedirect() {
    const { nodeId } = useParams<{ nodeId: string }>();
    const { search, hash } = useLocation();
    return <Navigate to={`/orders/${encodeURIComponent(nodeId ?? '')}${search}${hash}`} replace />;
}

/**
 * App routes
 * "/language"                          >> LANGUAGE FRAME
 * "/language/new"                      >> NEW LANGUAGE FRAME
 * "/language/:id"                      >> EDIT LANGUAGE id
 * "/products                           >> PRODUCTS WORKFLOW FRAME
 * "/hierarchies/*"                     >> HIERARCHIES FRAME
 * "/translations"                      >> TRANSLATIONS FRAME
 * "/hierarchies/:hierarchyId/:nodeId"  >> EDIT TRANSLATIONS
 */
export function Routes(): JSX.Element {
    const { hasUserRole } = useAuth();
    const { languages } = useLanguageCtx();
    const {
        enableContentTranslation,
        jsonataDependenciesEnabled,
        enableExternalCallLog,
        enableUserAnalytics,
        enableFindReplace,
        enableBulkChangeRules,
        variantPricingEnabled,
        defaultPageUri,
        cpq,
    } = useContext(SettingsContext);
    const { routes: companyRoutes, defaultRoute: companyDefaultRoute } = useCompanyOverrides();
    const canAccessHierarchies = useCanAccessHierarchies();
    const customPages = useCustomPages();

    const router = createBrowserRouter(
        createRoutesFromElements(
            // The route provider distils the router state the edit-node form reads, so the form
            // is not re-rendered by every navigation (see EditNodeRouteContext).
            <Route
                element={
                    <EditNodeRouteProvider>
                        <AnalyticsProvider>
                            <Layout />
                        </AnalyticsProvider>
                    </EditNodeRouteProvider>
                }
                errorElement={<RouteErrorPage />}
            >
                {/* Errors thrown by a page (including a failed lazy import) render the error page
                    inside the layout, so the app bar stays usable. */}
                <Route errorElement={<RouteErrorPage />}>
                    {hasUserRole('PIM_ADMIN') && (
                        <>
                            <Route path="language/new" element={<LanguageCreatePage />} />
                            <Route path="language/:id" element={<LanguageEditPage />} />
                            <Route path="language" element={<LanguageListPage />} />

                            <Route
                                path="products/"
                                index
                                element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                            />
                            <Route
                                path="products/:id"
                                index
                                element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                            />
                            <Route
                                path="products/edit/:id"
                                index
                                element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                            />
                            <Route
                                path="products/:id/:code"
                                index
                                element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                            />
                            <Route
                                path="products/:id/edit/:code"
                                index
                                element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                            />
                            <Route
                                path="products/:id/:code/:typecode"
                                index
                                element={languages ? <ProductsPage /> : <Navigate to="/language" />}
                            />
                        </>
                    )}
                    {hasUserRole('PIM_DEMO_3D') && (
                        <Route
                            path="3d-viewer"
                            element={languages ? <ThreeDViewerPage /> : <Navigate to="/language" />}
                        />
                    )}
                    {hasUserRole('CGT_PRICELIST') && (
                        <Route
                            path="short_lists"
                            element={
                                languages ? (
                                    <ShortListPage />
                                ) : (
                                    <Navigate
                                        to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                    />
                                )
                            }
                        />
                    )}
                    {(hasUserRole('CGT_PROTOTYPES') || hasUserRole('PIM_PROTOTYPE')) && (
                        <Route
                            path="prototypes/:cgtModelId"
                            element={
                                languages ? (
                                    <PrototypesPage />
                                ) : (
                                    <Navigate
                                        to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                    />
                                )
                            }
                        />
                    )}
                    {canAccessHierarchies && (
                        <Route
                            path="hierarchies/*"
                            index
                            element={
                                languages ? (
                                    <Hierarchies />
                                ) : (
                                    <Navigate
                                        to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                    />
                                )
                            }
                        />
                    )}
                    <Route
                        path="search"
                        element={
                            languages ? (
                                <SearchListPage />
                            ) : (
                                <Navigate
                                    to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                />
                            )
                        }
                    />
                    <Route
                        path="search/results/:objectSchema"
                        element={
                            languages ? (
                                <SearchDetailPage />
                            ) : (
                                <Navigate
                                    to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                />
                            )
                        }
                    />
                    <Route
                        path="discounts"
                        element={
                            languages ? (
                                <Discounts />
                            ) : (
                                <Navigate
                                    to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                />
                            )
                        }
                    />

                    <Route
                        path="jobs"
                        element={languages ? <JobListPage /> : <Navigate to="/language" />}
                    />
                    <Route
                        path="job-schedules"
                        element={languages ? <JobSchedulesPage /> : <Navigate to="/language" />}
                    />
                    {hasUserRole('PIM_SUPERADMIN') && (
                        <Route
                            path="settings"
                            element={
                                languages ? (
                                    <SettingsPage />
                                ) : (
                                    <Navigate
                                        to={
                                            hasUserRole('PIM_SUPERADMIN')
                                                ? '/language'
                                                : '/dashboard'
                                        }
                                    />
                                )
                            }
                        />
                    )}

                    {hasUserRole('PIM_SUPERADMIN') && (
                        <Route path="admin-dashboard" element={<AdminDashboardPage />} />
                    )}

                    {hasUserRole('COMPANY_ADMIN') && (
                        <Route path="company-admin" element={<CompanyAdminPage />} />
                    )}

                    {hasUserRole('PIM_CONFIGIT_ADMIN') && (
                        <Route path="configit" element={<ConfigitPage />} />
                    )}

                    {(hasUserRole('PIM_PRICING') || hasUserRole('PIM_ADMIN')) && (
                        <Route
                            path="data-quality"
                            element={
                                languages ? (
                                    <DataQualityPage />
                                ) : (
                                    <Navigate
                                        to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                    />
                                )
                            }
                        />
                    )}

                    {hasUserRole('PIM_CPQ') && (
                        <>
                            <Route
                                path="offers"
                                element={
                                    languages ? (
                                        <OffersPage />
                                    ) : (
                                        <Navigate
                                            to={
                                                hasUserRole('PIM_ADMIN')
                                                    ? '/language'
                                                    : '/dashboard'
                                            }
                                        />
                                    )
                                }
                            />
                            <Route
                                path="offers/:offerId"
                                element={
                                    languages ? (
                                        <OfferDetailPage />
                                    ) : (
                                        <Navigate
                                            to={
                                                hasUserRole('PIM_ADMIN')
                                                    ? '/language'
                                                    : '/dashboard'
                                            }
                                        />
                                    )
                                }
                            />
                            {cpq?.enableOrders && (
                                <>
                                    <Route
                                        path="orders"
                                        element={
                                            languages ? (
                                                <OrdersPage />
                                            ) : (
                                                <Navigate
                                                    to={
                                                        hasUserRole('PIM_ADMIN')
                                                            ? '/language'
                                                            : '/dashboard'
                                                    }
                                                />
                                            )
                                        }
                                    />
                                    <Route
                                        path="orders/:orderId"
                                        element={
                                            languages ? (
                                                <OrderDetailPage />
                                            ) : (
                                                <Navigate
                                                    to={
                                                        hasUserRole('PIM_ADMIN')
                                                            ? '/language'
                                                            : '/dashboard'
                                                    }
                                                />
                                            )
                                        }
                                    />
                                    {/* Links in the custom page form land on the native pages. */}
                                    <Route
                                        path="pages/orders"
                                        element={<Navigate to="/orders" replace />}
                                    />
                                    <Route
                                        path="pages/orders/:nodeId"
                                        element={<LegacyOrderDetailRedirect />}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {hasUserRole('PIM_ADMIN') && jsonataDependenciesEnabled === true && (
                        <Route
                            path="jsonata-dependencies"
                            element={
                                languages ? <JsonataDependencies /> : <Navigate to="/language" />
                            }
                        />
                    )}

                    {hasUserRole('PIM_ADMIN') && enableExternalCallLog === true && (
                        <Route
                            path="external-call-log"
                            element={
                                languages ? <ExternalCallLogPage /> : <Navigate to="/language" />
                            }
                        />
                    )}

                    {(hasUserRole('COMPANY_ADMIN') || hasUserRole('PIM_ADMIN')) &&
                        enableFindReplace === true &&
                        enableBulkChangeRules === true && (
                            <>
                                <Route
                                    path="bulk-change-rules"
                                    element={
                                        languages ? (
                                            <BulkChangeRulesPage />
                                        ) : (
                                            <Navigate to="/language" />
                                        )
                                    }
                                />
                                <Route
                                    path="bulk-change-rules/:ruleId"
                                    element={
                                        languages ? (
                                            <BulkChangeRulesPage />
                                        ) : (
                                            <Navigate to="/language" />
                                        )
                                    }
                                />
                            </>
                        )}

                    {hasUserRole('PIM_ADMIN') && enableUserAnalytics === true && (
                        <Route
                            path="user-analytics"
                            element={
                                languages ? <UserAnalyticsPage /> : <Navigate to="/language" />
                            }
                        />
                    )}

                    {hasUserRole(CPQ_PRICING_DEBUG_ROLE) && variantPricingEnabled === true && (
                        <Route
                            path="pricing-test-bench"
                            element={
                                languages ? <PricingTestBenchPage /> : <Navigate to="/language" />
                            }
                        />
                    )}

                    {/* Whether the agents are enabled is checked by /api/agents; the pages say why when not. */}
                    {hasUserRole(PIM_AGENT_USER_ROLE) && (
                        <>
                            <Route
                                path="agent-tasks"
                                element={
                                    languages ? <AgentTasksPage /> : <Navigate to="/language" />
                                }
                            />
                            <Route
                                path="agent-tasks/:runId"
                                element={
                                    languages ? <AgentRunDetailPage /> : <Navigate to="/language" />
                                }
                            />
                        </>
                    )}

                    <Route
                        path="pages/:slug"
                        element={
                            languages ? (
                                <CustomPageListPage />
                            ) : (
                                <Navigate
                                    to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                />
                            )
                        }
                    />
                    <Route
                        path="pages/:slug/:nodeId"
                        element={
                            languages ? (
                                <CustomPageDetailPage />
                            ) : (
                                <Navigate
                                    to={hasUserRole('PIM_ADMIN') ? '/language' : '/dashboard'}
                                />
                            )
                        }
                    />

                    {/* Available to every authenticated user: no role guard, no language prerequisite. */}
                    <Route path="profile" element={<UserProfilePage />} />

                    {companyRoutes.map(route => (
                        <Route key={route.path} path={route.path} element={<route.component />} />
                    ))}

                    <Route
                        path="*"
                        element={
                            <Navigate
                                to={getDefaultRoute({
                                    isAdmin: hasUserRole('PIM_ADMIN'),
                                    companyDefaultRoute,
                                    defaultPageUri,
                                    hasUserRole,
                                    canAccessHierarchies,
                                    firstCustomPageSlug: customPages[0]?.slug,
                                })}
                            />
                        }
                    />

                    {enableContentTranslation && (
                        <>
                            <Route path={translationsPath} element={<Translations />} />
                            <Route
                                path={`${translationsPath}/:hierarchyId/:nodeId`}
                                element={<Translations />}
                            />
                        </>
                    )}
                </Route>
            </Route>,
        ),
    );

    // let span = webTracerWithZone.startSpan(`Router`, contenxt.active());
    // router.subscribe(routeState => {
    //     if (span) {
    //         span.end();
    //     }
    //     span = webTracerWithZone.startSpan(`Router`);
    // });

    return (
        <Suspense fallback={<DelayedFallback />}>
            <RouterProvider router={router} />
        </Suspense>
    );
}

export default Routes;
